import { LitElement, html, css } from '@hh/global.js'

class HHInputWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: {
      reflect: true,
    },
    filled: {
      reflect: true,
    },
    label: {},
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    this.$select = this.querySelector(`select[slot='input']`)
    this.$select && this.preselectSelectValue()

    if (!!this.querySelector(`[slot='input']`).value && !this.$select) {
      this.filled = true
    }

    if (this.$select && this.label !== 'stacked') {
      this.filled = true
    }
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default
    }
  }

  onChange(e) {
    if (this.animation === 'none' || this.label === 'stacked') {
      return
    }
    this.filled = !!e.target.value
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = true
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = false
  }

  render() {
    return html`
      <slot name="label"></slot>
      <slot name="icon"></slot>
      <slot
        name="input"
        @change="${this.onChange}"
        @keydown="${this.onChange}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
      ></slot>
    `
  }

  static styles = css`
    ::slotted(label) {
      --hh-font-size-desktop: var(--hh-label-font-size-desktop);
      --hh-font-size-mobile: var(--hh-label-font-size-mobile);
      font: var(--hh-label-font-weight) var(--hh-font-size-fluid) /
        var(--hh-label-line-height) var(--hh-label-font-family);
      display: flex;
      align-items: center;
      position: absolute;
      color: var(--hh-color-subdued);
      padding: var(--hh-space-xs);
      transform: translateY(0);
      transition: transform var(--hh-duration-default, 300ms),
        font-size var(--hh-duration-default, 300ms);
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      width: 100%;
      height: 100%;
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      z-index: 2;
      text-indent: var(--hh-left-indent-small, 10px);
      color: currentColor;
      background: transparent;
      border: 0;
      font: var(--hh-body-font-weight) var(--hh-font-size-fluid) /
        var(--hh-body-line-height) var(--hh-body-font-family);
      letter-spacing: var(--hh-body-letter-spacing);
      border-radius: var(--hh-border-radius-sm);
      padding-top: 14px;
    }

    ::slotted(select) {
      appearance: none;
    }

    :host([focused='true']) ::slotted(label),
    :host([filled='true']) ::slotted(label) {
      transform: translateY(-10px);
    }

    :host([animation='disappear'][focused='true']) ::slotted(label),
    :host([animation='disappear'][filled='true']) ::slotted(label) {
      visibility: hidden;
      transform: translateY(0);
    }

    :host([focused='true']) ::slotted(input:focus-visible) {
      outline: none !important;
    }

    :host {
      position: relative;
      display: block;
      height: calc(var(--hh-height, 45px) + 2px);
      background: var(--hh-color-reverse-with-opacity);
      border: var(--hh-border-size, 1px) solid currentColor;
      border-radius: var(--hh-border-radius-sm);
    }

    :host([theme='minimal']) {
      background: transparent;
      border: none;
      border-bottom: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([label='stacked']) {
      border: none;
      height: auto;
    }

    :host([label='stacked']) ::slotted(label),
    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      position: relative;
    }

    :host([label='stacked']) ::slotted(label) {
      height: auto;
      display: block;
      margin: var(--hh-label-margin);
      padding-left: 0;
    }

    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      border: var(--hh-border-size, 1px) solid currentColor;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--hh-left-indent, 32px);
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--hh-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--hh-right-indent, 32px);
    }

    ::slotted([slot='icon']) {
      display: flex;
      position: absolute;
      width: var(--hh-icon-width, 23px);
      height: var(--hh-height, 45px);
      z-index: var(--hh-icon-z-index, 3);
    }

    :host([icon='right']) ::slotted([slot='icon']) {
      right: var(--hh-icon-right, 10px);
    }

    :host([height='multiline']),
    :host([height='multiline']) ::slotted(textarea) {
      height: var(--hh-multiline-height, 100px);
    }

    :host([height='compact']),
    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--hh-compact-height, 35px);
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--hh-compact-height, 35px);
    }
  `
}

customElements.define('hh-input-wrapper', HHInputWrapper)
